import * as React from "react";
import { useIntl } from "react-intl";
import { Form, Message, Button, Popup, Icon } from "semantic-ui-react";

import { BasicFormProps, Category, PromptCampaign } from "../../../interfaces";
import { serverDateUTCToDate, Rules, pstTimeToDate, sortPrompts } from "../../../utils";
import { MultipleDaySelectorComponent } from "@/components/MultipleDaySelectorComponent";
import { AddCategoryComponent } from "@/components/AddCategoryComponent";
import { PromptCards } from "@/components/Lists/PromptCardList";
import { DateRangePicker } from "@/components/DateRangePicker";

import { SocialBasicContentRow, SocialBasicRow, SocialGrid } from "../../../styling/baseStyle";
import { CampaignType, descriptors } from "./descriptors";
import "./styles.scss";
import { useMemo } from "react";
import moment from "moment-timezone";

interface OwnProps extends BasicFormProps {
  /**
   * Handle delivery mode changes
   */
  prompts: PromptCampaign[];
  submitRef: any;
  /**
   * To indicate if this section needs to be disabled for the current coach
   */
  disabled: boolean;
  /**
   * To indicate if this section can edit delivery date,
   * time and social networks in current prompts.
   */
  editable: boolean;

  /**
   * ID of previously included prompts
   */
  initialPromptsIds: string[];

  handleSetFieldValue?: (field: string, value: any) => void;
  addPromptHandler: () => void;
  handlePromptsSorted?: (campaignPrompts: PromptCampaign[]) => void;
}

type Props = OwnProps;

/**
 * Basic Info Step.
 */
const CampaignClass: React.FC<Props> = ({
  messages,
  values,
  prompts,
  submitRef,
  disabled,
  editable = true,
  initialPromptsIds,
  handleChange,
  handleSetFieldValue,
  handleSubmit,
  addPromptHandler,
  handlePromptsSorted,
}) => {
  const { deliveryMode, isAutoPost } = values;
  const deliveryRandom = deliveryMode !== "SPECIFIC_DATE";
  const { formatMessage } = useIntl();
  const [categories, setCategories] = React.useState<Category[]>(values.categories);
  const errors = messages && messages.error && messages.error.length > 0;

  const [promptsSorted, setPromptsSorted] = React.useState<PromptCampaign[]>(sortPrompts(prompts, deliveryRandom));

  const deliveryLabel = useMemo(() => {
    switch (deliveryMode) {
      case "RANDOM_DELIVERY":
        return "Randomized";
      case "ORDERED_RANDOM":
        return "Ordered";
      case "SPECIFIC_DATE":
        return "Scheduled";
    }
    return undefined;
  }, [deliveryMode]);

  React.useEffect(() => {
    handleSetFieldValue!("categories", categories);
  }, [categories]);

  const eligibleDaysAndTime = () => {
    const promptTime = values.deliveryTimeDatePicker || pstTimeToDate(values.randomScheduleDeliveryTimePst);
    return (
      <SocialBasicContentRow>
        <MultipleDaySelectorComponent
          editable={editable}
          description={""}
          selectedDays={values.randomScheduleDays}
          onChangeDays={days => editable && handleSetFieldValue!("randomScheduleDays", days)}
          selectedTime={promptTime}
          onChangeTime={(date: any) => {
            if (editable) {
              handleSetFieldValue!("deliveryTimeDatePicker", date);
              const timePst = moment(date).tz("America/Los_Angeles").format("HH:mm:00");
              handleSetFieldValue!("randomScheduleDeliveryTimePst", timePst);
            }
          }}
        />
      </SocialBasicContentRow>
    );
  };

  const customStartEnd = () => {
    const startDate = values.startDate ? serverDateUTCToDate(values.startDate) : values.startDate;
    const endDate = values.endDate ? serverDateUTCToDate(values.endDate) : values.endDate;
    return (
      <>
        <SocialBasicRow>
          <Popup
            hoverable
            trigger={
              <label>
                {formatMessage({ ...descriptors[CampaignType.durationSectionLabel] })}
                <Icon name="info circle" className={"infoIcon"} link />
              </label>
            }
            content={formatMessage({ ...descriptors[CampaignType.campaignDurationHoverDescription] })}
          />
        </SocialBasicRow>
        <SocialBasicContentRow>
          <DateRangePicker
            startDate={startDate}
            onChangeStartDate={date => handleSetFieldValue!("startDate", date)}
            endDate={endDate}
            onChangeEndDate={date => handleSetFieldValue!("endDate", date)}
          />
        </SocialBasicContentRow>
      </>
    );
  };

  // const isEditable =
  //   editable ||
  //   promptsSorted.reduce(
  //     (accumulator: boolean, currentValue) =>
  //       accumulator || initialPromptsIds.includes(`${currentValue.prompt!.promptId}`) === false,
  //     false
  //   );

  return (
    <Form size="large" error={errors} ref={submitRef} onSubmit={handleSubmit} className={"campaignInformationForm"}>
      <div className={disabled ? "formDiv disabledForm" : "formDiv"}>
        {messages && messages.error && messages.error.length > 0 && <Message error list={messages && messages.error} />}
        <SocialGrid>
          <SocialBasicRow>
            <label>Campaign Title</label>
          </SocialBasicRow>
          <SocialBasicContentRow>
            <Form.Input
              width={10}
              key={"campaignNameInput"}
              data-elm-id={"campaignNameInput"}
              fluid
              id={"name"}
              type={"text"}
              name={"name"}
              placeholder={formatMessage({ ...descriptors[CampaignType.titlePlaceholder] })}
              onChange={(e, data) => {
                if (e.target.value.length < Rules.MAX_CAMPAIGN_TITLE) {
                  handleChange!(e, data);
                }
              }}
              value={values.name}
            />
          </SocialBasicContentRow>
          <SocialBasicRow>
            <label>Categories</label>
          </SocialBasicRow>
          <SocialBasicContentRow>
            <AddCategoryComponent
              selectedCategories={categories}
              handleOnChange={setCategories}
              type={"CAMPAIGN"}
              multiple={false}
            />
          </SocialBasicContentRow>
          {deliveryRandom && (
            <>
              <SocialBasicRow>
                <label>Delivery ({deliveryLabel})</label>
              </SocialBasicRow>
              {eligibleDaysAndTime()}
              {customStartEnd()}
            </>
          )}
          <SocialBasicRow>
            <label>Prompts</label>
            <Button
              data-elm-id={`campaignInfoEditPromptsBtn`}
              type={"button"}
              onClick={addPromptHandler}
              floated={"right"}
              className={"secondary rounded editPromptsBtn"}
            >
              Edit Prompts
            </Button>
          </SocialBasicRow>
          <SocialBasicRow>
            <PromptCards
              fromEdit={true}
              editable={editable}
              initialPromptsIds={initialPromptsIds}
              promptsSorted={promptsSorted}
              isAutoPost={isAutoPost}
              deliveryMode={deliveryMode}
              values={values.prompts}
              handleSetFieldValue={(field: string, value: any) => {
                handleSetFieldValue!(`prompts.${field}`, value);
              }}
              handlePromptsSorted={final => {
                setPromptsSorted(final);
                if (handlePromptsSorted) {
                  handlePromptsSorted(final);
                }
              }}
            />
          </SocialBasicRow>
        </SocialGrid>
      </div>
    </Form>
  );
};

export const CampaignInformationForm = CampaignClass;
